import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, AlertCircle, Wrench, Search } from 'lucide-react';
import { format } from 'date-fns';
import { config } from '../config';

interface Incident {
  id: string;
  title: string;
  summary: string;
  severity: string;
  state: string;
  started_at: string;
  resolved_at?: string;
  components?: Array<{ id: string; display_name: string }>;
}

interface Maintenance {
  id: string;
  title: string;
  description: string;
  status: string;
  window_start: string;
  window_end: string;
  components?: Array<{ id: string; display_name: string }>;
}

export default function History() {
  const [activeTab, setActiveTab] = useState<'incidents' | 'maintenances'>('incidents');
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [stateFilter, setStateFilter] = useState<string>('all');

  const apiUrl = config.apiUrl;

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  async function fetchData() {
    setLoading(true);
    try {
      if (activeTab === 'incidents') {
        await fetchIncidents();
      } else {
        await fetchMaintenances();
      }
    } finally {
      setLoading(false);
    }
  }

  async function fetchIncidents() {
    try {
      const response = await fetch(`${apiUrl}/v1/stats/incidents/history?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setIncidents(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch incidents', error);
    }
  }

  async function fetchMaintenances() {
    try {
      const response = await fetch(`${apiUrl}/v1/stats/maintenances/history?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setMaintenances(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch maintenances', error);
    }
  }

  const filteredIncidents = incidents.filter(incident => {
    const matchesSearch = incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         incident.summary?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === 'all' || incident.severity === severityFilter;
    const matchesState = stateFilter === 'all' || incident.state === stateFilter;
    return matchesSearch && matchesSeverity && matchesState;
  });

  const filteredMaintenances = maintenances.filter(maintenance => {
    const matchesSearch = maintenance.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         maintenance.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-rose-50 text-rose-400';
      case 'MAJOR': return 'bg-amber-50 text-amber-500';
      case 'MINOR': return 'bg-yellow-50 text-yellow-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'RESOLVED': return 'bg-emerald-50 text-emerald-700';
      case 'MONITORING': return 'bg-blue-50 text-blue-700';
      case 'IDENTIFIED': return 'bg-purple-50 text-purple-700';
      case 'INVESTIGATING': return 'bg-yellow-50 text-yellow-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-emerald-50 text-emerald-700';
      case 'IN_PROGRESS': return 'bg-blue-50 text-blue-700';
      case 'SCHEDULED': return 'bg-purple-50 text-purple-700';
      case 'CANCELLED': return 'bg-gray-50 text-gray-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="bg-navy-900 rounded-2xl p-8">
        <h1 className="text-2xl font-bold text-white flex items-center space-x-2.5">
          <Calendar className="h-6 w-6 text-unlimit-lime" />
          <span>History</span>
        </h1>
        <p className="text-gray-400 mt-1.5 text-sm">
          View past incidents and maintenance activities
        </p>
      </div>

      {/* Content Card */}
      <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setActiveTab('incidents')}
            className={`flex-1 px-6 py-3.5 text-center text-sm font-semibold transition-all relative ${
              activeTab === 'incidents'
                ? 'text-navy-900'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <AlertCircle className="h-4 w-4" />
              <span>Incidents</span>
            </div>
            {activeTab === 'incidents' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-unlimit-lime" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('maintenances')}
            className={`flex-1 px-6 py-3.5 text-center text-sm font-semibold transition-all relative ${
              activeTab === 'maintenances'
                ? 'text-navy-900'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Wrench className="h-4 w-4" />
              <span>Maintenances</span>
            </div>
            {activeTab === 'maintenances' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-unlimit-lime" />
            )}
          </button>
        </div>

        {/* Filters */}
        <div className="p-5 bg-unlimit-gray-light border-b border-gray-100">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-unlimit-lime/50 focus:border-unlimit-lime transition-all outline-none"
                />
              </div>
            </div>

            {activeTab === 'incidents' && (
              <>
                <div className="w-full md:w-44">
                  <select
                    value={severityFilter}
                    onChange={(e) => setSeverityFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-unlimit-lime/50 focus:border-unlimit-lime transition-all outline-none appearance-none"
                  >
                    <option value="all">All Severities</option>
                    <option value="CRITICAL">Critical</option>
                    <option value="MAJOR">Major</option>
                    <option value="MINOR">Minor</option>
                  </select>
                </div>
                <div className="w-full md:w-44">
                  <select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-unlimit-lime/50 focus:border-unlimit-lime transition-all outline-none appearance-none"
                  >
                    <option value="all">All States</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="MONITORING">Monitoring</option>
                    <option value="IDENTIFIED">Identified</option>
                    <option value="INVESTIGATING">Investigating</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="relative">
                <div className="w-10 h-10 border-2 border-gray-200 rounded-full" />
                <div className="absolute inset-0 w-10 h-10 border-2 border-transparent border-t-unlimit-lime rounded-full animate-spin" />
              </div>
            </div>
          ) : activeTab === 'incidents' ? (
            <div className="space-y-3">
              {filteredIncidents.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <AlertCircle className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm font-semibold text-gray-500">No incidents found</p>
                  <p className="text-xs mt-1">Try adjusting your filters</p>
                </div>
              ) : (
                filteredIncidents.map((incident) => (
                  <Link
                    key={incident.id}
                    to={`/incidents/${incident.id}`}
                    className="block bg-white border border-gray-100 rounded-xl p-5 hover:shadow-card-hover hover:border-gray-200 transition-all duration-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center flex-wrap gap-2 mb-1.5">
                          <h3 className="text-sm font-bold text-navy-900">{incident.title}</h3>
                          <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-md ${getSeverityColor(incident.severity)}`}>
                            {incident.severity}
                          </span>
                          <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-md ${getStateColor(incident.state)}`}>
                            {incident.state}
                          </span>
                        </div>
                        {incident.summary && (
                          <p className="text-xs text-gray-500 mb-2.5 line-clamp-2">{incident.summary}</p>
                        )}
                        {incident.components && incident.components.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2.5">
                            {incident.components.map((comp) => (
                              <span key={comp.id} className="px-2 py-0.5 bg-unlimit-gray-light text-gray-500 text-[11px] font-medium rounded-md">
                                {comp.display_name}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-4 text-[11px] text-gray-400">
                          <div className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>Started: {format(new Date(incident.started_at), 'PPp')}</span>
                          </div>
                          {incident.resolved_at && (
                            <div className="flex items-center space-x-1">
                              <Clock className="h-3 w-3" />
                              <span>Resolved: {format(new Date(incident.resolved_at), 'PPp')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMaintenances.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Wrench className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm font-semibold text-gray-500">No maintenance activities found</p>
                  <p className="text-xs mt-1">Try adjusting your search</p>
                </div>
              ) : (
                filteredMaintenances.map((maintenance) => (
                  <Link
                    key={maintenance.id}
                    to={`/maintenance/${maintenance.id}`}
                    className="block bg-white border border-gray-100 rounded-xl p-5 hover:shadow-card-hover hover:border-gray-200 transition-all duration-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center flex-wrap gap-2 mb-1.5">
                          <h3 className="text-sm font-bold text-navy-900">{maintenance.title}</h3>
                          <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-md ${getStatusColor(maintenance.status)}`}>
                            {maintenance.status.replace('_', ' ')}
                          </span>
                        </div>
                        {maintenance.description && (
                          <p className="text-xs text-gray-500 mb-2.5 line-clamp-2">{maintenance.description}</p>
                        )}
                        {maintenance.components && maintenance.components.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2.5">
                            {maintenance.components.map((comp) => (
                              <span key={comp.id} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[11px] font-medium rounded-md">
                                {comp.display_name}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-4 text-[11px] text-gray-400">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>Start: {format(new Date(maintenance.window_start), 'PPp')}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>End: {format(new Date(maintenance.window_end), 'PPp')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
